import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useLocation, useNavigate } from 'react-router-dom';
import logger from 'src/utils/logger';
import ContainerBody from '../components/ui/ContainerOverlay/ContainerBody/ContainerBody';
import { OverlayActions, useOverlay } from '../providers/OverlayProvider';
import Button, { ButtonToneProps } from '../components/ui/Button/Button';
import { ContactOverlayForm, CreateContactFormValues } from '../components/layouts/ContactOverlay/ContactOverlayForm';
import { PartnerInput, useContactCreate } from '../services/contact';
import Divider from '../components/ui/Divider/Divider';

export function CreateContactOverlay() {
  const { t } = useTranslation();
  const mutation = useContactCreate();
  const navigate = useNavigate();
  const location = useLocation();
  const { onClose } = useOverlay();

  const formRef = useRef<HTMLFormElement>(null);

  const onSubmit = async (data: CreateContactFormValues) => {
    logger.info('Creating contact');
    try {
      const result = await mutation.mutateAsync({
        body: data as PartnerInput,
      });
      const id = result.id as number;

      toast.success(t('contact.success.created'));

      navigate(`${location.pathname.startsWith('/settings/contacts') ? '/settings/contacts' : '/contact'}/${id}`);
    } catch (e) {
      logger.error('Failed to create contact', e);
      toast.error(t('contact.error.create'));
    }
  };

  const triggerSubmit = () => {
    formRef.current?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
  };

  return (
    <>
      <OverlayActions>
        <Button title={t('general.cancel')} onClick={() => onClose()} tone={ButtonToneProps.SECONDARY} />
        <Button title={t('general.save')} tone={ButtonToneProps.PRIMARY_BLUE} onClick={triggerSubmit} />
      </OverlayActions>
      <ContainerBody className="h-full">
        <div className="flex md:flex-row gap-16 md:items-center items-start mb-12 justify-between flex-col">
          <h2 className="text-full typo-title-36">{t('contact.create_contact')}</h2>
        </div>
        <Divider className="my-24" />
        <div className="text-full typo-text-16--bold mb-20">{t('general.contact_details')}</div>
        <ContactOverlayForm mode="create" ref={formRef} onSubmit={onSubmit} readOnly={false} />
      </ContainerBody>
    </>
  );
}
